import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import produce from 'immer';
// import deepmerge from 'deepmerge';
import { jwtDecode } from 'jwt-decode';
import logger from '../common/logger';

// import XHRError from '../common/xhrError';
import authLib from '../common/pkce';
import { LoginClickHandler } from '../common/login';
import { RETRY_COUNT } from '../common/constants';
import { convertToBoolean, getLearnLiveServiceHost, isNotEmpty } from '../common/utilities';
import { getUserTimezone, getUser24Hour } from '../common/utilitiesTime';
import { createLocalStorage } from '../common/storage';

const storage = createLocalStorage();

axiosRetry(axios, { retries: RETRY_COUNT });
export const fetchUserData = createAsyncThunk(
  'userData',
  async () => {
    const auth = authLib.authClient;

    try {
      const { accessToken, idToken } = await new Promise((resolve, reject) => {
        auth.getAccessTokenSilently()
          .catch((e) => {
            reject(e);
            return null;
          })
          .then((at) => {
            if (at) {
              auth.getIDTokenSilently()
                .catch((e) => {
                  reject(e);
                  return null;
                })
                .then((it) => {
                  if (it) {
                    resolve({ accessToken: at, idToken: it });
                  }
                });
            }
          });
      });

      // Update login state for UH
      // window.headerConfig.accessToken = accessToken;
      // const evt = new CustomEvent('SSO_SIGNED_IN', { detail: { at: accessToken } });
      // window.dispatchEvent(evt);

      const decodedIdToken = jwtDecode(idToken);
      const mappedData = {
        userid: decodedIdToken.userid,
        oa2Token: accessToken,
        first: decodedIdToken.first_name,
        last: decodedIdToken.last_name,
        email: decodedIdToken.user_email,
      };

      return Promise.resolve(mappedData);
    } catch (e) {
      if (!e.toString().includes('Login required')) {
        logger.error('PKCE ERROR w/ Login Flow:', e);
      }
      return Promise.reject(new Error('No PKCE Login'));
    }
  },
);

// https://learnlive-enrollment-api-<environment alias>.autodesk.com/coachingrequest/coach/identity/v1/{userId}
export const fetchUserRoles = createAsyncThunk(
  'fetchUserRoles',
  async ({ env, token }, { rejectWithValue }) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    const identityRequestUrl = `${getLearnLiveServiceHost({ env })}/coachingrequest/coach/identity/v1`;
    try {
      if (!token) {
        return {};
      }
      const response = await axios.get(
        identityRequestUrl,
        {
          headers: {
            ...headers,
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

/** @type {import('@reduxjs/toolkit').Slice} */
export const userSlice = createSlice({
  name: 'userData',
  initialState: {
    userDetails: {},
    userRoles: {
      identity: {
        roles: [],
        email: undefined,
        userId: undefined,
        coachingTypes: [],
        firstName: undefined,
        lastName: undefined,
        enhancedToken: undefined,
      },
    },
    userSettings: {
      preferredTimezone: getUserTimezone(),
      preferred24Hour: getUser24Hour(),
    },
    isLoaderVisible: false,
    authChecked: false,
  },
  reducers: {
    setUserDetails: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.userDetails = action?.payload || {};
        draftState.userRoles = {};
      });
      return newState;
    },
    setUserPreferredTimezone: (state, action) => {
      const newState = produce(state, (draftState) => {
        if (action.payload) {
          draftState.userSettings = draftState.userSettings || {};
          draftState.userSettings.preferredTimezone = action.payload;
          storage.setItem('userPreferredTimezone', action.payload.name);
        }
      });
      return newState;
    },
    setUserPreferred24Hour: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.userSettings = draftState.userSettings || {};
        draftState.userSettings.preferred24Hour = action.payload;
        storage.setItem('userPreferred24Hour', action.payload);
      });
      return newState;
    },
    updateAccessToken: (state, action) => {
      const newState = produce(state, (draftState) => {
        if (action.payload && draftState.userDetails) {
          draftState.userDetails.oa2Token = action.payload;
        }
      });
      return newState;
    },
  },
  extraReducers: {
    [fetchUserData.pending]: (state) => ({
      ...state,
    }),
    [fetchUserData.fulfilled]: (state, action) => {
      logger.info('fetchUserData.fulfilled');
      // User data has been returned.
      const newState = {
        isLoaderVisible: false,
        authChecked: true,
      };

      newState.userDetails = action?.payload || {};
      return {
        ...state,
        ...newState,
      };
    },
    [fetchUserData.rejected]: (state, err) => {
      // User is not logged in. Ensure removal of user-data from store.
      logger.info('fetchUserData.rejected - User was not logged in', err);
      LoginClickHandler();
      return {
        ...state,
        userDetails: {},
        isLoaderVisible: false,
        authChecked: true,
      };
    },
    [fetchUserRoles.pending]: (state) => ({
      ...state,
    }),
    [fetchUserRoles.fulfilled]: (state, action) => {
      logger.info('fetchUserRoles.fulfilled');
      // User data has been returned.
      const newState = {
        isLoaderVisible: false,
      };
      newState.userRoles = action?.payload || {};
      return {
        ...state,
        ...newState,
      };
    },
    [fetchUserRoles.rejected]: (state, err) => {
      // User is not logged in. Ensure removal of user-data from store.
      logger.info('fetchUserRoles.rejected', err);
      return {
        ...state,
        userRoles: {},
        isLoaderVisible: false,
        authChecked: true,
      };
    },
  },
});
export const {
  setUserDetails,
  setUserPreferredTimezone,
  setUserPreferred24Hour,
  updateAccessToken,
} = userSlice.actions;
export const getUserDetails = (state) => state.userData.userDetails;
export const getUserRoles = (state) => state.userData.userRoles;
export const getUserRoleCoachingTypes = (state) => state.userData.userRoles.identity.coachingTypes;
export const getUserId = (state) => state.userData.userDetails.userid;
export const getOA2Token = (state) => state.userData.userDetails.oa2Token;
export const getOA2TokenWithClaims = (state) => state.userData.userRoles.identity.enhancedToken;
export const getIsUserLoggedIn = (state) => state.userData.authChecked === true && isNotEmpty(state.userData.userDetails.userid);

export const getUserPreferredTimezone = (state) => state.userData?.userSettings?.preferredTimezone;

export const getUserPreferred24Hour = (state) => convertToBoolean({ value: state.userData?.userSettings?.preferred24Hour });

export const getIsUserRolesLoaded = (state) => state.userData?.userRoles?.identity?.userId?.length > 0;

export const getIsUserViewOnly = (state) => state.userData?.userRoles?.identity?.roles?.some((role) => role.label === 'View-Only');
export const getIsUserCoach = (state) => state.userData?.userRoles?.identity?.roles?.some((role) => role?.label?.toLowerCase() === 'coach');
export const getIsUserCoachingManager = (state) => state.userData?.userRoles?.identity?.roles?.some((role) => role?.label?.toLowerCase() === 'coaching manager');
export const getIsUserSpecialistLead = (state) => state.userData?.userRoles?.identity?.roles?.some((role) => role?.label?.toLowerCase() === 'specialist lead');
export const getIsUserManager = (state) => state.userData?.userRoles?.identity?.roles?.some((role) => role?.label?.toLowerCase() === 'manager');
export default userSlice.reducer;
